import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	AlertCircle,
	Building2,
	Check,
	Loader2,
	Mail,
	Shield,
	UserPlus,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";

// Local types
interface Invitation {
	id: string;
	email: string;
	role: "owner" | "admin" | "viewer";
	invitedBy: string;
	expiresAt: string;
	token: string;
}


export const Route = createFileRoute("/invite/$token")({
	component: InviteAcceptPage,
});

function InviteAcceptPage() {
	const { token } = Route.useParams();
	const navigate = useNavigate();
	const { user, login } = useAuth();
	const trpc = useTRPC();
	const queryClient = useQueryClient();

	// All useState hooks must be at the top, before any early returns
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<string>("register");
	const [registerForm, setRegisterForm] = useState({
		firstName: "",
		lastName: "",
		email: "",
		password: "",
		confirmPassword: "",
	});
	const [loginForm, setLoginForm] = useState({
		email: "",
		password: "",
	});
	const [errors, setErrors] = useState<Record<string, string>>({});

	// Query invitation by token
	const { data: invitationData, isLoading: invitationLoading, error: invitationError } = useQuery(
		trpc.members.getByToken.queryOptions({ token }),
	);

	// Accept invitation mutation (for new account creation)
	const acceptMutation = useMutation(
		trpc.members.acceptInvitation.mutationOptions({
			onSuccess: async (data) => {
				// Account created and invitation accepted - now log the user in
				try {
					const success = await login(data.email, registerForm.password);
					if (success) {
						toast.success("Account created! Welcome to the organization.");
						queryClient.invalidateQueries({ queryKey: trpc.organizations.getAccessibleOrgs.queryKey() });
						navigate({ to: "/solicitations" });
					} else {
						toast.success("Account created! Please sign in to continue.");
						navigate({ to: "/buyers/login" });
					}
				} catch {
					toast.success("Account created! Please sign in to continue.");
					navigate({ to: "/buyers/login" });
				}
				setIsLoading(false);
			},
			onError: (error) => {
				toast.error(`Failed to accept invitation: ${error.message}`);
				setIsLoading(false);
			},
		}),
	);

	// Accept invitation for logged-in user mutation
	const acceptForLoggedInMutation = useMutation(
		trpc.members.acceptInvitationLoggedIn.mutationOptions({
			onSuccess: () => {
				toast.success("Invitation accepted! Welcome to the organization.");
				queryClient.invalidateQueries({ queryKey: trpc.organizations.getAccessibleOrgs.queryKey() });
				navigate({ to: "/solicitations" });
				setIsLoading(false);
			},
			onError: (error) => {
				toast.error(`Failed to accept invitation: ${error.message}`);
				setIsLoading(false);
			},
		}),
	);

	// Map to local type - ensure all values are strings, not objects
	const invitation: Invitation | null = invitationData && !invitationData.isExpired && !invitationData.isAccepted
		? {
				id: invitationData.id,
				email: invitationData.email,
				role: invitationData.role as "owner" | "admin" | "viewer",
				invitedBy: invitationData.invitedBy?.first_name && invitationData.invitedBy?.last_name
					? `${invitationData.invitedBy.first_name} ${invitationData.invitedBy.last_name}`
					: (typeof invitationData.invitedBy?.email === 'string' ? invitationData.invitedBy.email : "Unknown"),
				expiresAt: invitationData.expiresAt,
				token: token,
			}
		: null;

	// Organization name - ensure it's always a string
	const organizationName: string =
		typeof invitationData?.organization?.name === 'string'
			? invitationData.organization.name
			: "Organization";

	// Update activeTab based on user state (after hooks, before early returns)
	const effectiveActiveTab = user ? "accept" : activeTab;

	// Update form email when invitation data loads
	const invitationEmail = invitation?.email || invitationData?.email || "";

	// Show loading state
	if (invitationLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
				<Card className="w-full max-w-md">
					<CardContent className="py-8 flex items-center justify-center">
						<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
					</CardContent>
				</Card>
			</div>
		);
	}

	// If invitation not found or already accepted
	if (!invitationData || invitationData.isAccepted) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
							<AlertCircle className="h-6 w-6 text-red-600" />
						</div>
						<CardTitle className="text-2xl font-bold">
							{invitationData?.isAccepted ? "Invitation Already Accepted" : "Invalid Invitation"}
						</CardTitle>
						<CardDescription>
							{invitationData?.isAccepted
								? "This invitation has already been accepted."
								: "This invitation link is invalid or has expired. Please contact your administrator for a new invitation."}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button
							className="w-full"
							onClick={() => navigate({ to: "/buyers/login" })}
						>
							Go to Login
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// If invitation not found after mapping (expired)
	if (!invitation) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
							<AlertCircle className="h-6 w-6 text-yellow-600" />
						</div>
						<CardTitle className="text-2xl font-bold">
							Invitation Expired
						</CardTitle>
						<CardDescription>
							This invitation has expired. Please contact the person who invited you for a new invitation.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button
							className="w-full"
							onClick={() => navigate({ to: "/buyers/login" })}
						>
							Go to Login
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Check if invitation is expired
	const isExpired = new Date(invitation.expiresAt) < new Date();
	if (isExpired) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
							<AlertCircle className="h-6 w-6 text-yellow-600" />
						</div>
						<CardTitle className="text-2xl font-bold">
							Invitation Expired
						</CardTitle>
						<CardDescription>
							This invitation has expired on{" "}
							{new Date(invitation.expiresAt).toLocaleDateString()}. Please
							contact {invitation.invitedBy} for a new invitation.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button
							className="w-full"
							onClick={() => navigate({ to: "/buyers/login" })}
						>
							Go to Login
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const getRoleBadgeColor = (role: string) => {
		switch (role) {
			case "owner":
				return "bg-purple-100 text-purple-700";
			case "admin":
				return "bg-blue-100 text-blue-700";
			case "viewer":
				return "bg-gray-100 text-gray-700";
			default:
				return "bg-gray-100 text-gray-700";
		}
	};

	const validateRegisterForm = () => {
		const newErrors: Record<string, string> = {};

		if (!registerForm.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}
		if (!registerForm.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}
		if (!registerForm.password) {
			newErrors.password = "Password is required";
		} else if (registerForm.password.length < 8) {
			newErrors.password = "Password must be at least 8 characters";
		}
		if (registerForm.password !== registerForm.confirmPassword) {
			newErrors.confirmPassword = "Passwords do not match";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleRegisterAndAccept = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!validateRegisterForm()) return;

		setIsLoading(true);
		try {
			acceptMutation.mutate({
				token: token,
				firstName: registerForm.firstName,
				lastName: registerForm.lastName,
				password: registerForm.password,
			});
		} catch (_error) {
			toast.error("Failed to create account. Please try again.");
			setIsLoading(false);
		}
	};

	const handleLoginAndAccept = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		try {
			const success = await login(loginForm.email, loginForm.password);
			if (success) {
				// After login, accept the invitation
				acceptForLoggedInMutation.mutate({ token });
			}
		} catch (_error) {
			toast.error("Invalid email or password.");
			setIsLoading(false);
		}
	};

	const handleAcceptInvitation = async () => {
		setIsLoading(true);
		acceptForLoggedInMutation.mutate({ token });
	};

	const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setRegisterForm((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setLoginForm((prev) => ({ ...prev, [name]: value }));
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<Card className="w-full max-w-lg">
				<CardHeader className="text-center">
					<div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
						<UserPlus className="h-6 w-6 text-primary" />
					</div>
					<CardTitle className="text-2xl font-bold">
						You've been invited!
					</CardTitle>
					<CardDescription>
						{invitation.invitedBy} has invited you to join their organization
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* Invitation Details */}
					<div className="bg-muted/50 rounded-lg p-4 space-y-3">
						<div className="flex items-center gap-3">
							<Building2 className="h-5 w-5 text-muted-foreground" />
							<div>
								<p className="text-sm text-muted-foreground">Organization</p>
								<p className="font-medium">{organizationName}</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<Mail className="h-5 w-5 text-muted-foreground" />
							<div>
								<p className="text-sm text-muted-foreground">Invited Email</p>
								<p className="font-medium">{invitation.email}</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<Shield className="h-5 w-5 text-muted-foreground" />
							<div>
								<p className="text-sm text-muted-foreground">Your Role</p>
								<Badge className={getRoleBadgeColor(invitation.role)}>
									{invitation.role.charAt(0).toUpperCase() +
										invitation.role.slice(1)}
								</Badge>
							</div>
						</div>
					</div>

					{/* If user is already logged in, show accept button */}
					{user ? (
						<div className="space-y-4">
							<div className="bg-green-50 border border-green-200 rounded-lg p-4">
								<div className="flex items-center gap-2">
									<Check className="h-5 w-5 text-green-600" />
									<p className="text-sm text-green-700">
										You're logged in as <strong>{user.email}</strong>
									</p>
								</div>
							</div>
							<Button
								className="w-full"
								onClick={handleAcceptInvitation}
								disabled={isLoading}
							>
								{isLoading ? "Accepting..." : "Accept Invitation"}
							</Button>
						</div>
					) : (
						/* If not logged in, show register/login tabs */
						<Tabs value={activeTab} onValueChange={setActiveTab}>
							<TabsList className="grid w-full grid-cols-2">
								<TabsTrigger value="register">Create Account</TabsTrigger>
								<TabsTrigger value="login">I Have an Account</TabsTrigger>
							</TabsList>

							<TabsContent value="register" className="mt-4">
								<form onSubmit={handleRegisterAndAccept} className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<div className="space-y-2">
											<Label htmlFor="firstName">First Name</Label>
											<Input
												id="firstName"
												name="firstName"
												placeholder="John"
												value={registerForm.firstName}
												onChange={handleRegisterChange}
												aria-invalid={!!errors.firstName}
											/>
											{errors.firstName && (
												<p className="text-sm text-red-500">
													{errors.firstName}
												</p>
											)}
										</div>
										<div className="space-y-2">
											<Label htmlFor="lastName">Last Name</Label>
											<Input
												id="lastName"
												name="lastName"
												placeholder="Doe"
												value={registerForm.lastName}
												onChange={handleRegisterChange}
												aria-invalid={!!errors.lastName}
											/>
											{errors.lastName && (
												<p className="text-sm text-red-500">
													{errors.lastName}
												</p>
											)}
										</div>
									</div>

									<div className="space-y-2">
										<Label htmlFor="email">Email</Label>
										<Input
											id="email"
											name="email"
											type="email"
											value={invitationEmail}
											disabled
											className="bg-muted"
										/>
										<p className="text-xs text-muted-foreground">
											This email was specified in your invitation
										</p>
									</div>

									<div className="space-y-2">
										<Label htmlFor="password">Password</Label>
										<Input
											id="password"
											name="password"
											type="password"
											placeholder="Create a password"
											value={registerForm.password}
											onChange={handleRegisterChange}
											aria-invalid={!!errors.password}
										/>
										{errors.password && (
											<p className="text-sm text-red-500">{errors.password}</p>
										)}
									</div>

									<div className="space-y-2">
										<Label htmlFor="confirmPassword">Confirm Password</Label>
										<Input
											id="confirmPassword"
											name="confirmPassword"
											type="password"
											placeholder="Confirm your password"
											value={registerForm.confirmPassword}
											onChange={handleRegisterChange}
											aria-invalid={!!errors.confirmPassword}
										/>
										{errors.confirmPassword && (
											<p className="text-sm text-red-500">
												{errors.confirmPassword}
											</p>
										)}
									</div>

									<Button type="submit" className="w-full" disabled={isLoading}>
										{isLoading
											? "Creating account..."
											: "Create Account & Accept"}
									</Button>
								</form>
							</TabsContent>

							<TabsContent value="login" className="mt-4">
								<form onSubmit={handleLoginAndAccept} className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="loginEmail">Email</Label>
										<Input
											id="loginEmail"
											name="email"
											type="email"
											placeholder="your.email@example.com"
											value={loginForm.email}
											onChange={handleLoginChange}
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="loginPassword">Password</Label>
										<Input
											id="loginPassword"
											name="password"
											type="password"
											placeholder="Enter your password"
											value={loginForm.password}
											onChange={handleLoginChange}
										/>
									</div>

									<Button type="submit" className="w-full" disabled={isLoading}>
										{isLoading ? "Signing in..." : "Sign In & Accept"}
									</Button>

									<p className="text-center text-sm text-muted-foreground">
										<a
											href="/buyers/forgot-password"
											className="text-primary underline underline-offset-4 hover:text-primary/80"
										>
											Forgot your password?
										</a>
									</p>
								</form>
							</TabsContent>
						</Tabs>
					)}

					<p className="text-xs text-center text-muted-foreground">
						By accepting this invitation, you agree to join {organizationName}{" "}
						and follow their organization policies.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
